import React, { useState, useEffect } from 'react';
import { isValidObjectId } from '../utils/mongoUtils';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Map as MapIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Check as CheckIcon,
  Timeline as TimelineIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import AuthLayout from '../components/AuthLayout';
import routeService from '../services/route.service';
import serviceRequestService from '../services/serviceRequest.service';
import RouteMapComponent from '../components/RouteMapComponent';

const RouteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [route, setRoute] = useState(null);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  
  // Função para carregar detalhes da rota
  const fetchRouteDetails = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (!id) {
        setError('ID da rota não fornecido');
        setLoading(false);
        return;
      }
      
      console.log(`Carregando detalhes da rota com ID: ${id}`);
      
      // @ts-ignore - string | undefined não é atribuível ao tipo string
      const response = await routeService.getRouteById(id);
      
      if ('error' in response) {
        console.error(`Erro ao carregar rota ${id}:`, response.error);
        setError(`Erro ao carregar rota: ${response.error}`);
        setLoading(false);
        return;
      }
      
      if (!response.data) {
        console.error(`Resposta vazia para a rota ${id}`);
        setError('Resposta vazia do servidor. Tente novamente mais tarde.');
        setLoading(false);
        return;
      }
      
      console.log(`Rota ${id} carregada com sucesso:`, response.data);
      setRoute(response.data);
      
      // Usar os dados dos chamados que já estão incluídos na resposta da rota
      if (response.data.serviceRequests && response.data.serviceRequests.length > 0) {
        console.log(`A rota tem ${response.data.serviceRequests.length} chamados associados`);
        
        // Verificar se os chamados já estão populados (objetos completos) ou são apenas IDs
        const isPopulated = typeof response.data.serviceRequests[0] === 'object' && response.data.serviceRequests[0] !== null;
        
        if (isPopulated) {
          console.log('Os chamados já estão populados na resposta da rota');
          
          // Usar diretamente os dados dos chamados da resposta
          const requests = response.data.serviceRequests.map((request: any) => {
            console.log('Processando chamado da resposta:', request);
            
            return {
              ...request,
              _id: request._id,
              status: request.status || 'waiting',
              description: request.description || 'Sem descrição',
              number: request.number || `ID: ${String(request._id).substring(0, 8)}...`
            };
          });
          
          // @ts-ignore - Tipo any[] não é atribuível ao tipo SetStateAction<never[]>
          setServiceRequests(requests);
          setSuccess('Chamados carregados com sucesso!');
        } else {
          // Se os chamados não estiverem populados, precisamos carregá-los individualmente
          const requestIds: string[] = response.data.serviceRequests;
          const requests: any[] = [];
          let loadErrors = 0;
          
          console.log(`Carregando ${requestIds.length} chamados de serviço associados à rota`);
          
          // Usar Promise.allSettled para carregar todos os chamados em paralelo
          const requestPromises = requestIds.map((requestId: string) => {
            console.log(`Iniciando carregamento do chamado ${requestId}`);
            return serviceRequestService.getServiceRequestById(requestId)
              .then(response => {
                console.log(`Chamado ${requestId} carregado com sucesso:`, response);
                return response;
              })
              .catch(error => {
                console.error(`Erro ao carregar chamado ${requestId}:`, error);
                return { 
                  error: error.message || 'Erro desconhecido', 
                  _id: requestId,
                  number: `ID: ${String(requestId).substring(0, 8)}...`,
                  description: 'Erro ao carregar chamado',
                  status: 'error'
                };
              });
          });
          
          const results = await Promise.allSettled(requestPromises);
          
          results.forEach((result, index) => {
            const requestId = requestIds[index];
            
            if (result.status === 'fulfilled') {
              const requestResponse = result.value;
              
              // Verificar se a resposta contém um erro
              if ('error' in requestResponse) {
                console.error(`Erro ao carregar chamado ${requestId}:`, requestResponse.error);
                loadErrors++;
                // Adicionar o objeto de chamado com erro
                requests.push({
                  ...requestResponse,
                  _id: requestId,
                  error: requestResponse.error,
                  status: requestResponse.status || 'error',
                  description: requestResponse.description || 'Erro ao carregar detalhes',
                  number: requestResponse.number || `ID: ${String(requestId).substring(0, 8)}...`
                });
              } else {
                console.log(`Chamado ${requestId} carregado com sucesso:`, requestResponse);
                // Garantir que o objeto tenha todos os campos necessários
                requests.push({
                  ...requestResponse,
                  _id: requestResponse._id || requestId,
                  status: requestResponse.status || 'waiting',
                  description: requestResponse.description || 'Sem descrição',
                  number: requestResponse.number || `ID: ${String(requestId).substring(0, 8)}...`
                });
              }
            } else {
              console.error(`Erro ao carregar chamado ${requestId}:`, result.reason);
              loadErrors++;
              // Adicionar um objeto de chamado com erro
              requests.push({
                _id: requestId,
                error: result.reason ? (result.reason as Error).message || 'Erro desconhecido' : 'Erro ao carregar chamado',
                status: 'error',
                description: 'Erro ao carregar detalhes',
                number: `ID: ${String(requestId).substring(0, 8)}...`
              });
            }
          });
          
          // @ts-ignore - Tipo any[] não é atribuível ao tipo SetStateAction<never[]>
          setServiceRequests(requests);
          
          // Mostrar aviso se houver erros
          if (loadErrors > 0) {
            setError(`Não foi possível carregar ${loadErrors} chamado(s) completamente. Alguns detalhes podem estar incompletos, mas os dados básicos foram carregados.`);
          } else {
            setSuccess('Todos os chamados foram carregados com sucesso!');
          }}
      } else {
        console.log('Nenhum chamado associado a esta rota');
      }
    } catch (error: any) {
      console.error('Erro ao carregar detalhes da rota:', error);
      setError(`Não foi possível carregar os detalhes da rota: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Chamar a função quando o componente for montado
  useEffect(() => {
    if (id) {
      fetchRouteDetails();
    } else {
      setError('ID da rota não fornecido');
      setLoading(false);
    }
  }, [id]);
  
  // Iniciar uma rota
  const handleStartRoute = async () => {
    try {
      // @ts-ignore - Esperado 2 argumentos, mas recebeu 1
      await routeService.startRoute(id);
      setSuccess('Rota iniciada com sucesso!');
      
      // Atualizar o estado da rota
      // @ts-ignore - Spread types só podem ser criados a partir de tipos de objeto
      setRoute(prev => ({ ...prev, status: 'in_progress' }));
    } catch (error) {
      console.error('Erro ao iniciar rota:', error);
      setError('Não foi possível iniciar a rota. Tente novamente.');
    }
  };
  
  // Completar uma rota
  const handleCompleteRoute = async () => {
    try {
      // @ts-ignore - string | undefined não é atribuível ao tipo string
      await routeService.completeRoute(id, {});
      setSuccess('Rota finalizada com sucesso!');
      
      // Atualizar o estado da rota
      // @ts-ignore - Spread types só podem ser criados a partir de tipos de objeto
      setRoute(prev => ({ ...prev, status: 'completed' }));
    } catch (error) {
      console.error('Erro ao finalizar rota:', error);
      setError('Não foi possível finalizar a rota. Tente novamente.');
    }
  };
  
  // Excluir uma rota
  const handleDeleteRoute = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta rota?')) return;
    
    setLoading(true);
    try {
      // @ts-ignore - string | undefined não é atribuível ao tipo string
      const response = await routeService.deleteRoute(id);
      
      if (response.error) {
        console.error('Erro ao excluir rota:', response.error);
        setError(`Não foi possível excluir a rota: ${response.error}`);
      } else {
        console.log('Resposta da API ao excluir rota:', response);
        setSuccess('Rota excluída com sucesso!');
        
        // Redirecionar para a lista de rotas após um breve delay
        setTimeout(() => {
          navigate('/routes');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Erro ao excluir rota:', error);
      setError(`Não foi possível excluir a rota: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Renderizar o status da rota
  const renderRouteStatus = () => {
    if (!route) return null;
    
    const statusConfig = {
      scheduled: { label: 'Agendada', color: 'primary', icon: <CalendarIcon /> },
      in_progress: { label: 'Em Execução', color: 'warning', icon: <PlayArrowIcon /> },
      completed: { label: 'Concluída', color: 'success', icon: <CheckIcon /> },
      cancelled: { label: 'Cancelada', color: 'error', icon: <DeleteIcon /> }
    };
    
    // @ts-ignore - Tipo any não pode ser usado para indexar o tipo statusConfig
    const config = statusConfig[route.status] || { label: route.status, color: 'default', icon: <TimelineIcon /> };
    
    return (
      <Chip 
        icon={config.icon}
        label={config.label} 
        color={config.color} 
        sx={{ fontWeight: 'medium' }}
      />
    );
  };
  
  // Renderizar lista de chamados na rota
  const renderServiceRequests = () => {
    console.log('Renderizando chamados:', serviceRequests);
    if (serviceRequests.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Nenhum chamado associado a esta rota.
        </Typography>
      );
    }
    
    return (
      <List>
        {serviceRequests.map((request, index) => {
          // Encontrar o waypoint correspondente para obter a ordem
          let waypointIndex = -1;
          
          // Verificar se route, optimizedWaypoints e request existem
          if (route && (route as any).optimizedWaypoints && request && (request as any)._id) {
            waypointIndex = (route as any).optimizedWaypoints.findIndex((wp: any) => 
              wp && wp.id === (request as any)._id
            );
          }
          
          console.log(`Waypoint index para chamado ${request ? (request as any)._id : 'undefined'}: ${waypointIndex}`);
          
          // Gerar uma chave única para o fragmento, mesmo se o request for undefined
          const requestKey = request && (request as any)._id ? (request as any)._id : `request-${index}`;
          
          return (
            <React.Fragment key={requestKey}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    onClick={() => {
                      // Verificar se o request existe e se o ID é válido antes de navegar
                      if (request && (request as any)._id && isValidObjectId((request as any)._id)) {
                        navigate(`/service-requests/${(request as any)._id}`);
                      } else {
                        const errorId = request ? (request as any)._id || 'undefined' : 'undefined';
                        setError(`ID inválido: ${errorId} não é um ObjectId válido do MongoDB ou o chamado não foi carregado corretamente`);
                      }
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: waypointIndex === 0 ? '#4caf50' : '#2196f3' }}>
                    {waypointIndex !== -1 ? waypointIndex + 1 : '?'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ mr: 1 }}>
                        {request && (request as any).number ? (request as any).number : 
                         (request && (request as any)._id ? 
                          (typeof (request as any)._id === 'string' ? 
                            `ID: ${(request as any)._id.substring(0, 8)}...` : 
                            (typeof (request as any)._id === 'object' ? 
                              `ID: ${(request as any)._id.toString ? (request as any)._id.toString().substring(0, 8) : 'Objeto'}...` : 
                              `ID: ${String((request as any)._id).substring(0, 8)}...`)) : 'N/A')}
                      </Typography>
                      <Chip 
                        label={request && (request as any).status ? 
                              ((request as any).status === 'waiting' ? 'Aguardando' : 
                               (request as any).status === 'in_progress' ? 'Em Execução' : 
                               (request as any).status === 'completed' ? 'Concluído' : 
                               (request as any).status === 'cancelled' ? 'Cancelado' : 
                               (request as any).status === 'error' ? 'Erro' : 
                               (request as any).status) : 
                              (request && 'error' in request ? 'Erro' : 'Status desconhecido')}
                        size="small" 
                        color={request && (request as any).status ? 
                               ((request as any).status === 'waiting' ? 'warning' : 
                                (request as any).status === 'in_progress' ? 'info' : 
                                (request as any).status === 'completed' ? 'success' : 
                                (request as any).status === 'cancelled' ? 'error' : 
                                (request as any).status === 'error' ? 'error' : 
                                'default') : 
                               (request && 'error' in request ? 'error' : 'default')}
                      />
                      {request && 'error' in request && (
                        <Tooltip title={(request as any).error}>
                          <IconButton size="small" color="error">
                            <ErrorIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" component="span">
                        {request && (request as any).title ? (request as any).title : 
                         (request && (request as any).description ? 
                           (typeof (request as any).description === 'string' ? 
                             (request as any).description : 
                             (typeof (request as any).description === 'object' ? 
                               JSON.stringify((request as any).description) : 
                               String((request as any).description))) : 
                         (request && 'error' in request ? 'Erro ao carregar detalhes' : 'Sem título'))}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {request && (request as any).address?.street ? 
                          `${(request as any).address.street}${(request as any).address.number ? `, ${(request as any).address.number}` : ''}` : 
                          (request && (request as any).location?.address ? 
                            (typeof (request as any).location.address === 'string' ? 
                              (request as any).location.address : 
                              'Endereço complexo') : 
                          (request && (request as any).client?.name ? 
                            `Cliente: ${typeof (request as any).client.name === 'string' ? 
                                       (request as any).client.name : 
                                       'Nome complexo'}` : 
                          (request && 'error' in request ? 'Erro ao carregar endereço' : 'Endereço não informado')))}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    );
  };
  
  if (loading) {
    return (
      <AuthLayout title="Carregando Rota...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }
  
  if (!route) {
    return (
      <AuthLayout title="Rota Não Encontrada">
        <Box sx={{ width: '100%' }}>
          <Alert severity="error">
            Rota não encontrada ou você não tem permissão para acessá-la.
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/routes')}
            sx={{ mt: 2 }}
          >
            Voltar para Lista
          </Button>
        </Box>
      </AuthLayout>
    );
  }
  
  return (
    <AuthLayout title="Detalhes da Rota">
      <Box sx={{ width: '100%' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate('/routes')} 
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {(route as any).name}
            </Typography>
          </Box>
          
          <Box>
            {renderRouteStatus()}
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Descrição
                  </Typography>
                  <Typography variant="body1">
                    {(route as any).description || 'Nenhuma descrição fornecida.'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1" color="text.secondary">
                      Técnico Responsável
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {(route as any).technician?.displayName || 'Não atribuído'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1" color="text.secondary">
                      Data de Execução
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {format(new Date((route as any).executionDate), 'dd/MM/yyyy', { locale: ptBR })}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1" color="text.secondary">
                      Distância Total
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {(route as any).totalDistance ? `${((route as any).totalDistance / 1000).toFixed(1)} km` : 'Não calculado'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TimelineIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1" color="text.secondary">
                      Tempo Estimado
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {(route as any).totalDuration ? `${Math.floor((route as any).totalDuration / 60)} minutos` : 'Não calculado'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Ponto de Partida
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<MapIcon />}
                      onClick={() => setMapDialogOpen(true)}
                    >
                      Ver no Mapa
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Coordenadas: {(route as any).startLocation?.lat.toFixed(6)}, {(route as any).startLocation?.lng.toFixed(6)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            <Paper elevation={1} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Chamados na Rota
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<RefreshIcon />}
                  onClick={fetchRouteDetails}
                  disabled={loading}
                >
                  Recarregar Chamados
                </Button>
              </Box>
              {renderServiceRequests()}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                mb: 3, 
                height: '250px', 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.2,
                  zIndex: 0,
                  bgcolor: '#f5f5f5'
                }}
              >
                {/* Substituído por uma imagem estática para evitar problemas com o mapa */}
                <Box 
                  sx={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <MapIcon sx={{ fontSize: 80, color: '#ccc' }} />
                </Box>
              </Box>
              
              <Box sx={{ zIndex: 1, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Visualização da Rota
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<MapIcon />}
                  onClick={() => setMapDialogOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Ver Mapa Completo
                </Button>
              </Box>
            </Paper>
            
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ações
              </Typography>
              
              <Grid container spacing={2}>
                {(route as any).status === 'scheduled' && (
                  <>
                    <Grid item xs={12}>
                      <Button 
                        fullWidth
                        variant="contained" 
                        color="warning"
                        startIcon={<PlayArrowIcon />}
                        onClick={handleStartRoute}
                      >
                        Iniciar Rota
                      </Button>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Button 
                        fullWidth
                        variant="outlined" 
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/routes/${id}/edit`)}
                      >
                        Editar Rota
                      </Button>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Button 
                        fullWidth
                        variant="outlined" 
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteRoute}
                      >
                        Excluir Rota
                      </Button>
                    </Grid>
                  </>
                )}
                
                {(route as any).status === 'in_progress' && (
                  <Grid item xs={12}>
                    <Button 
                      fullWidth
                      variant="contained" 
                      color="success"
                      startIcon={<CheckIcon />}
                      onClick={handleCompleteRoute}
                    >
                      Finalizar Rota
                    </Button>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Button 
                    fullWidth
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/routes')}
                  >
                    Voltar para Lista
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Diálogo para visualizar a rota no mapa */}
        <Dialog
          open={mapDialogOpen}
          onClose={() => setMapDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          TransitionProps={{
            onExited: () => {
              // Garantir que qualquer recurso do mapa seja limpo após a transição de fechamento
              console.log('Diálogo de mapa fechado completamente');
            }
          }}
        >
          <DialogTitle>Visualização da Rota - {(route as any).name}</DialogTitle>
          <DialogContent>
            <Box sx={{ height: '70vh', width: '100%' }}>
              {mapDialogOpen && (
                <RouteMapComponent 
                  key={`map-${id}-${mapDialogOpen}`}
                  startLocation={(route as any).startLocation}
                  waypoints={(route as any).optimizedWaypoints}
                  directions={(route as any).directions}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMapDialogOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AuthLayout>
  );
};

export default RouteDetail;
